import { onPageIn, onMount, onDestroy } from '@/modules/_'
import gsap from 'gsap'

export default function (element: HTMLElement, dataset: DOMStringMap) {
  let customCursor: HTMLElement | null = null
  let isHovering = false
  let animationFrame: number | null = null
  let mouseX = 0
  let mouseY = 0
  let currentX = 0
  let currentY = 0
  let isActive = false

  const checkIfDesktop = () => {
    return window.matchMedia('(min-width: 1024px) and (pointer: fine)').matches
  }

  const lerp = (start: number, end: number, factor: number) => {
    return start + (end - start) * factor
  }

  const createCustomCursor = () => {
    const cursorSelector = element.querySelector('[data-cursor]') as HTMLElement

    if (cursorSelector) {
      customCursor = cursorSelector

      customCursor.style.position = 'fixed'
      customCursor.style.pointerEvents = 'none'
      customCursor.style.zIndex = '9999'
      customCursor.style.opacity = '0'
      customCursor.style.transition = 'opacity 0.3s ease'

      return
    }

    const cursorText = element.getAttribute('data-custom-cursor-text') || ''
    const hasArrow = element.hasAttribute('data-arrow')

    customCursor = document.createElement('div')
    customCursor.style.cssText = `
      position: fixed;
      pointer-events: none;
      z-index: 9999;
      opacity: 0;
      transform: translate(-50%, -50%);
      background: #FCFCF3;
      color: #1A1A1A;
      padding: 8px 15px;
      border-radius: 50px;
      display: flex;
      align-items: center;
      gap: 9px;
      text-transform: uppercase;
      font-size: 9px;
      font-weight: 500;
      white-space: nowrap;
      transition: opacity 0.3s ease;
    `

    const textSpan = document.createElement('span')
    textSpan.textContent = cursorText
    customCursor.appendChild(textSpan)

    if (hasArrow) {
      const arrowSvg = `
        <svg width="19" height="29" viewBox="0 0 19 29" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M7.62744 28.1836H2.0357V22.5908H7.62744V28.1836ZM14.3551 19.8151L10.4011 23.7691L6.44721 19.8151L10.4011 15.8612L14.3551 19.8151ZM8.76332 14.2234L4.80939 18.1783L0.855469 14.2234L4.80939 10.2695L8.76332 14.2234ZM18.8311 17.0203H13.2393V11.4285H18.8311V17.0203ZM14.3964 8.63167L10.4425 12.5856L6.48752 8.63167L10.4425 4.67674L14.3964 8.63167ZM7.62744 5.77533H2.0357V0.183594H7.62744V5.77533Z" fill="currentColor"/>
        </svg>
      `
      const arrowContainer = document.createElement('div')
      arrowContainer.style.cssText =
        'width: 7px; height: 10px; display: flex; align-items: center; justify-content: center;'
      arrowContainer.innerHTML = arrowSvg
      customCursor.appendChild(arrowContainer)
    }

    document.body.appendChild(customCursor)
  }

  const removeCustomCursor = () => {
    if (customCursor && customCursor.parentElement) {
      customCursor.remove()
      customCursor = null
    }
  }

  const activateCursor = () => {
    if (!isActive) {
      isActive = true
      createCustomCursor()
    }
  }

  const deactivateCursor = () => {
    if (isActive) {
      isActive = false
      isHovering = false

      element.style.cursor = ''

      if (animationFrame) {
        cancelAnimationFrame(animationFrame)
        animationFrame = null
      }

      removeCustomCursor()
    }
  }

  const handleResize = () => {
    const isDesktop = checkIfDesktop()

    if (isDesktop && !isActive) {
      activateCursor()
    } else if (!isDesktop && isActive) {
      deactivateCursor()
    }
  }

  const animateCursor = () => {
    if (!customCursor || !isHovering) return

    currentX = lerp(currentX, mouseX, 0.15)
    currentY = lerp(currentY, mouseY, 0.15)

    customCursor.style.left = `${currentX}px`
    customCursor.style.top = `${currentY}px`

    animationFrame = requestAnimationFrame(animateCursor)
  }

  const handleMouseMove = (e: MouseEvent) => {
    if (!isActive) return

    mouseX = e.clientX
    mouseY = e.clientY

    if (isHovering && !animationFrame) {
      animateCursor()
    }
  }

  const handleMouseEnter = (e: MouseEvent) => {
    if (!customCursor || !isActive) return

    isHovering = true
    mouseX = e.clientX
    mouseY = e.clientY
    currentX = mouseX
    currentY = mouseY

    customCursor.style.left = `${currentX}px`
    customCursor.style.top = `${currentY}px`
    customCursor.style.opacity = '1'

    const cursorSelector = element.querySelector('[data-cursor]')
    if (cursorSelector) {
      element.style.cursor = 'none'
    }

    animateCursor()
  }

  const handleMouseLeave = () => {
    if (!customCursor || !isActive) return

    isHovering = false
    customCursor.style.opacity = '0'

    element.style.cursor = ''

    if (animationFrame) {
      cancelAnimationFrame(animationFrame)
      animationFrame = null
    }
  }

  onMount(() => {
    if (checkIfDesktop()) {
      activateCursor()
    }

    element.addEventListener('mouseenter', handleMouseEnter)
    element.addEventListener('mousemove', handleMouseMove)
    element.addEventListener('mouseleave', handleMouseLeave)
    window.addEventListener('resize', handleResize)
  })

  onDestroy(() => {
    if (animationFrame) {
      cancelAnimationFrame(animationFrame)
    }

    element.removeEventListener('mouseenter', handleMouseEnter)
    element.removeEventListener('mousemove', handleMouseMove)
    element.removeEventListener('mouseleave', handleMouseLeave)
    window.removeEventListener('resize', handleResize)

    element.style.cursor = ''

    removeCustomCursor()
  })
}
